import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./SPXOptionsCalls.css";

const PAGE_SIZE = 12;

export default function SPXOptionsCalls() {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("today");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);

const today = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Kolkata",
  year: "numeric",
  month: "2-digit",
  day: "2-digit"
}).format(new Date());
  useEffect(() => {
    loadCalls();
  }, []);

  const loadCalls = async () => {
    try {
      const res = await axios.get("/api/spx-options");
      setCalls(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load SPX calls:", err);
    } finally {
      setLoading(false);
    }
  };

  const calculatePL = (buy, sell) => {
    const b = Number(buy);
    const s = Number(sell);

    if (buy == null || sell == null || !b) return null;

    return (((s - b) / b) * 100).toFixed(2);
  };

  const getDate = (date) => String(date || "").slice(0, 10);

  const isActive = (status) =>
    String(status || "").toLowerCase() === "active";

  const filteredCalls = useMemo(() => {
    return calls.filter((call) => {
      const date = getDate(call.date);

      const matchesTab =
        tab === "today"
          ? date === today && isActive(call.status)
          : date < today || !isActive(call.status);

      const matchesSearch =
        `${call.strike} ${call.type} ${call.expiry} ${call.notes || ""}`
          .toLowerCase()
          .includes(search.toLowerCase());

      const matchesType =
        typeFilter === "ALL" || call.type === typeFilter;

      const matchesStatus =
        statusFilter === "ALL" ||
        String(call.status || "").toLowerCase() ===
          statusFilter.toLowerCase();

      const matchesFrom = !fromDate || date >= fromDate;
      const matchesTo = !toDate || date <= toDate;

      return (
        matchesTab &&
        matchesSearch &&
        matchesType &&
        matchesStatus &&
        matchesFrom &&
        matchesTo
      );
    }).sort((a, b) =>
      getDate(b.date).localeCompare(getDate(a.date))
    );
  }, [calls, tab, today, search, typeFilter, statusFilter, fromDate, toDate]);

  useEffect(() => {
    setPage(1);
  }, [tab, search, typeFilter, statusFilter, fromDate, toDate]);

  const totalPages = Math.max(1, Math.ceil(filteredCalls.length / PAGE_SIZE));

  const visibleCalls = filteredCalls.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  if (loading) {
    return (
      <section className="spx-options-section">
        <div className="spx-options-container">
          <p>Loading SPX Options...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="spx-options-section">
      <div className="spx-options-container">

        <div className="spx-options-header">
          <span className="spx-label">TRENDMARK OPTIONS</span>
          <h2>SPX Options Calls</h2>
          <p>Track daily trading calls and review historical performance.</p>
        </div>

        <div style={{
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          margin: "25px 0"
        }}>
          <button
            onClick={() => setTab("today")}
            style={tabStyle(tab === "today")}
          >
            Today's Calls ({calls.filter(c =>
              getDate(c.date) === today && isActive(c.status)
            ).length})
          </button>

          <button
            onClick={() => setTab("history")}
            style={tabStyle(tab === "history")}
          >
            Historical Calls ({calls.filter(c =>
              getDate(c.date) < today || !isActive(c.status)
            ).length})
          </button>
        </div>

        <div style={{
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 25
        }}>
          <input
            placeholder="Search strike, expiry..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={inputStyle}
          />

          <select
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            style={inputStyle}
          >
            <option value="ALL">All Types</option>
            <option value="CALL">CALL</option>
            <option value="PUT">PUT</option>
          </select>

          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            style={inputStyle}
          >
            <option value="ALL">All Status</option>
            <option value="Active">Active</option>
            <option value="Closed">Closed</option>
            <option value="Target Hit">Target Hit</option>
            <option value="Stop Loss">Stop Loss</option>
          </select>

          <input
            type="date"
            value={fromDate}
            onChange={e => setFromDate(e.target.value)}
            style={inputStyle}
          />

          <input
            type="date"
            value={toDate}
            onChange={e => setToDate(e.target.value)}
            style={inputStyle}
          />

          <button
            onClick={() => {
              setSearch("");
              setTypeFilter("ALL");
              setStatusFilter("ALL");
              setFromDate("");
              setToDate("");
            }}
            style={tabStyle(false)}
          >
            Reset Filters
          </button>
        </div>

        <div style={{
          marginBottom: 20,
          color: "#aab2c0"
        }}>
          Showing {filteredCalls.length} calls
        </div>

        {visibleCalls.length === 0 ? (
          <div style={{
            textAlign: "center",
            padding: 50,
            borderRadius: 15,
            background: "rgba(255,255,255,0.03)"
          }}>
            <h3>No calls found</h3>
            <p>No records match your selected filters.</p>
          </div>
        ) : (
          <div className="spx-options-grid">
            {visibleCalls.map(call => {
              const pnl = calculatePL(
                call.buy_price,
                call.sell_price
              );

              return (
                <div
                  key={call.id}
                  className={`spx-trade-card ${
                    call.type === "CALL" ? "spx-call" : "spx-put"
                  }`}
                >
                  <div className="spx-card-top">
                    <div>
                      <small>{call.date}</small>
                      <h3>SPX {call.strike} {call.type}</h3>
                    </div>

                    <span className={`spx-status ${
                      isActive(call.status) ? "spx-active" : "spx-closed"
                    }`}>
                      {call.status}
                    </span>
                  </div>

                  <div className="spx-direction">
                    {call.type === "CALL" ? "🟢 BUY CALL" : "🔴 BUY PUT"}
                  </div>

                  <div className="spx-trade-info">
                    <div>
                      <span>Expiry</span>
                      <strong>{call.expiry}</strong>
                    </div>

                    <div>
                      <span>Buy Price</span>
                      <strong>${Number(call.buy_price).toFixed(2)}</strong>
                    </div>

                    <div>
                      <span>Sell Price</span>
                      <strong>
                        {call.sell_price != null
                          ? `$${Number(call.sell_price).toFixed(2)}`
                          : "OPEN"}
                      </strong>
                    </div>

                    <div>
                      <span>Stop Loss</span>
                      <strong>
                        {call.stop_loss != null
                          ? `$${Number(call.stop_loss).toFixed(2)}`
                          : "-"}
                      </strong>
                    </div>
                  </div>

                  {call.notes && (
                    <div style={{
                      marginTop: 15,
                      color: "#aab2c0"
                    }}>
                      {call.notes}
                    </div>
                  )}

                  {pnl !== null && (
                    <div className={
                      Number(pnl) >= 0 ? "spx-profit" : "spx-loss"
                    }>
                      Result: {Number(pnl) >= 0 ? "+" : ""}{pnl}%
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {filteredCalls.length > PAGE_SIZE && (
          <div style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 20,
            margin: "30px 0"
          }}>
            <button
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
              style={tabStyle(false)}
            >
              Previous
            </button>

            <span>Page {page} of {totalPages}</span>

            <button
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
              style={tabStyle(false)}
            >
              Next
            </button>
          </div>
        )}

        <div className="spx-risk">
          <strong>Risk Disclaimer:</strong> Options trading involves
          substantial risk. All calls are for educational and
          informational purposes only and are not financial advice.
        </div>

      </div>
    </section>
  );
}

const tabStyle = (active) => ({
  padding: "12px 22px",
  borderRadius: 9,
  border: active ? "1px solid #38bdf8" : "1px solid #444",
  background: active ? "#12334a" : "#171e29",
  color: "#fff",
  cursor: "pointer",
  fontWeight: 600
});

const inputStyle = {
  padding: "11px 13px",
  borderRadius: 8,
  border: "1px solid #444",
  background: "#171e29",
  color: "#fff",
  minWidth: 130
};
